import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { parseEdgeFunctionError } from '@trade-connector/shared';
import { supabase } from '../lib/supabase';
import { turnOffWebPushBeforeSignOut } from '../lib/webPush';
import type { User } from '../contexts/auth.types';

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error && error.message ? error.message : fallback;
};

/**
 * auth-actions answers 429 `{ error_code: 'RATE_LIMITED' }` once an address or a network has
 * asked for too many auth emails. That is an expected, handled condition: callers show the
 * server's message and log it with console.warn (console.error feeds Issue Center reports).
 */
class RateLimitedError extends Error {}

const rateLimitedMessage = async (error: unknown): Promise<string | null> => {
  if (!error || typeof error !== 'object') return null;
  const parsed = await parseEdgeFunctionError(error);
  return parsed.code === 'RATE_LIMITED' ? parsed.message : null;
};

export const clearStaleAuthStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('trade-connector-auth');
    const keys = Object.keys(window.localStorage);
    keys.forEach((key) => {
      if (
        key.startsWith('sb-') ||
        key.startsWith('supabase.auth.token') ||
        // Project-scoped keys used by src/lib/supabase.ts
        key.startsWith('tc-auth-')
      ) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear stale Supabase auth storage', error);
  }
};

export function useAuthActions(
  setUser: Dispatch<SetStateAction<User | null>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  loadUserProfile: (userId: string, userEmail?: string | null) => Promise<User | null>
) {
  const queryClient = useQueryClient();
  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      // Manually load user profile to ensure state updates immediately
      if (data.session?.user) {
        try {
          const userData = await loadUserProfile(data.session.user.id, data.session.user.email);
          if (userData) {
            setUser(userData);
          } else {
            // Fallback if profile load returns null but login succeeded
            console.warn('⚠️ [AuthContext] Profile load returned null; setting fallback user with baseline permissions');
            setUser({
              id: data.session.user.id,
              email: data.session.user.email || '',
              first_name: '',
              last_name: '',
              name: data.session.user.email?.split('@')[0] || 'User',
              roles: [{ role_name: 'USER', role_description: 'Basic User' }],
              permissions: ['user:view'],
              account_type: 'platform',
              company_id: null,
              customer_id: null,
              trade_company_id: null,
              is_platform_admin: false,
              customer_status: null,
              trade_company_status: null,
              direct_jobs_enabled: false,
            });
          }
        } catch (profileError) {
          console.warn('⚠️ [AuthContext] Profile load failed after login; setting fallback user with baseline permissions', profileError);
          // Vital: Set user state even if profile load fails, otherwise UI stays on login page
          setUser({
            id: data.session.user.id,
            email: data.session.user.email || '',
            first_name: '',
            last_name: '',
            name: data.session.user.email?.split('@')[0] || 'User',
            roles: [{ role_name: 'USER', role_description: 'Basic User' }],
            permissions: ['user:view'],
            account_type: 'platform',
            company_id: null,
            customer_id: null,
            trade_company_id: null,
            is_platform_admin: false,
            customer_status: null,
            trade_company_status: null,
          });
        }
      }

      return true;
    } catch (error) {
      console.error('💥 [AuthContext] login() threw', error);
      throw error;
    }
  }, [loadUserProfile, setUser]);

  const signup = useCallback(async (email: string, password: string, name: string, additionalData?: {
    companyType: 'customer' | 'trade_company';
    companyName?: string;
    firstName: string;
    lastName: string;
    phone: string;
    abn?: string;
    businessStructure?: string;
    businessAddress?: string;
    businessSuburb?: string;
    businessState?: string;
    businessPostcode?: string;
    businessLatitude?: number | null;
    businessLongitude?: number | null;
    businessType?: string;
    individualTradingName?: string;
    tradingName?: string;
    gstRegistered?: boolean;
    specializations?: string[];
    yearsInBusiness?: number;
    employeesCount?: number;
    businessDescription?: string;
    tradingAsIndividual?: boolean;
    is_field_worker?: boolean;
    referralCode?: string;
    crmLeadId?: string;
    teaserToken?: string;
    termsAccepted?: boolean;
  }): Promise<boolean> => {
    setIsLoading(true);
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      if (!additionalData?.companyType) {
        throw new Error('Account type is required');
      }

      const metaData = {
        first_name: additionalData?.firstName || name.split(' ')[0],
        last_name: additionalData?.lastName || name.split(' ').slice(1).join(' '),
        company_type: additionalData?.companyType,
        company_name: additionalData?.companyName,
        phone: additionalData?.phone,
        abn: additionalData?.abn,
        business_structure: additionalData?.businessStructure,
        business_address: additionalData?.businessAddress,
        business_suburb: additionalData?.businessSuburb,
        business_state: additionalData?.businessState,
        business_postcode: additionalData?.businessPostcode,
        business_latitude: additionalData?.businessLatitude ?? null,
        business_longitude: additionalData?.businessLongitude ?? null,
        business_type: additionalData?.businessType,
        individual_trading_name: additionalData?.individualTradingName,
        trading_name: additionalData?.tradingName,
        gst_registered: additionalData?.gstRegistered,
        specializations: additionalData?.specializations,
        years_in_business: additionalData?.yearsInBusiness,
        employees_count: additionalData?.employeesCount,
        business_description: additionalData?.businessDescription,
        trading_as_individual: additionalData?.tradingAsIndividual,
        is_field_worker: additionalData?.is_field_worker,
        referral_code: additionalData?.referralCode,
        crm_lead_id: additionalData?.crmLeadId,
        teaser_token: additionalData?.teaserToken,
        terms_accepted: additionalData?.termsAccepted === true,
      };

      const { data, error } = await supabase.functions.invoke('auth-actions', {
        body: {
          action: 'signup',
          email,
          password,
          data: metaData,
          redirect_to: `${window.location.origin}/auth/callback`
        }
      });

      if (data?.error) {
        throw new Error(typeof data.error === 'string' ? data.error : (data.error.message || data.error.msg || 'Signup failed'));
      }

      if (error) {
        const parsed = await parseEdgeFunctionError(error);

        if (parsed.code === 'RATE_LIMITED') {
          throw new RateLimitedError(parsed.message);
        }
        console.error('[AuthContext] Edge function error:', error);

        if (
          parsed.code === 'USER_EXISTS' ||
          parsed.code === 'user_already_exists' ||
          parsed.code === 'email_exists' ||
          parsed.message.toLowerCase().includes('already been registered') ||
          parsed.message.toLowerCase().includes('already registered') ||
          parsed.message.toLowerCase().includes('user already exists')
        ) {
          throw new Error('User already exists');
        }

        throw new Error(parsed.message);
      }

      return true;
    } catch (error) {
      if (error instanceof RateLimitedError) {
        console.warn('Signup rate limited:', error.message);
      } else {
        console.error('Signup error:', error);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const resetPassword = useCallback(async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('auth-actions', {
        body: {
          action: 'reset_password',
          email,
          redirect_to: `${window.location.origin}/reset-password`
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { success: true, message: 'Password reset email sent!' };
    } catch (error) {
      const limited = await rateLimitedMessage(error);
      if (limited) {
        console.warn('Reset password rate limited:', limited);
        return { success: false, message: limited };
      }
      console.error('Reset password error:', error);
      return { success: false, message: 'Failed to send reset email' };
    }
  }, []);

  const logout = useCallback(async () => {
    // Signing out switches this browser's push notifications off. It needs the
    // session, so it runs first; it never throws and gives up after a few seconds.
    await turnOffWebPushBeforeSignOut();

    try {
      await queryClient.cancelQueries();
    } catch (err) {
      console.warn('⚠️ Failed to cancel queries on logout:', err);
    }
    queryClient.clear();
    setUser(null);
    clearStaleAuthStorage();

    // Sign out this browser only. A global sign-out revokes every session for
    // the account, which logs the same user out of the mobile app (its edge
    // function calls then fail with "Session not found").
    void supabase.auth.signOut({ scope: 'local' }).then(({ error }) => {
      if (error) {
        console.warn('⚠️ Local signOut error (ignored):', error);
      }
    }).catch((err) => {
      console.warn('⚠️ Local signOut threw (ignored):', err);
    });
  }, [setUser, queryClient]);

  const resendConfirmationEmail = useCallback(async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('auth-actions', {
        body: {
          action: 'resend_confirmation',
          email,
          redirect_to: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { success: true, message: 'Confirmation email resent!' };
    } catch (error: unknown) {
      const limited = await rateLimitedMessage(error);
      if (limited) {
        console.warn('Resend confirmation rate limited:', limited);
        return { success: false, message: limited };
      }
      console.error('Resend confirmation error:', error);
      return { success: false, message: getErrorMessage(error, 'Failed to resend confirmation email') };
    }
  }, []);

  return {
    login,
    signup,
    resetPassword,
    logout,
    resendConfirmationEmail,
  };
}
